/**
 * Simple FHIR Dashboard - Robust Version
 * Displays basic FHIR resources statistics without complex features
 */

import { useQuery } from "@tanstack/react-query";
import {
  Activity,
  AlertTriangle,
  FileText,
  RefreshCw,
  Shield,
  Stethoscope,
} from "lucide-react";
import React from "react";

import { Button } from "@/components/ui/Button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/Card";
import { fhirApi } from "@/services/api/fhirApi";
import { useI18nStore } from "@/stores/i18nStore";
import type { FHIRResource } from "@/types/fhir";

const SimpleFHIRDashboard: React.FC = () => {
  const { t } = useI18nStore();
  // Fetch FHIR resources using available bundle endpoint
  const {
    data: bundle,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["fhir", "observations"],
    queryFn: () => fhirApi.getObservations(),
    refetchOnWindowFocus: false,
  });

  // Bundle entries may omit `resource`, so filter before asserting the type.
  const resources: FHIRResource[] = (bundle?.entry ?? [])
    .map((entry) => entry.resource)
    .filter((r): r is FHIRResource => r !== undefined);

  // Calculate statistics
  const totalResources = resources.length;
  const observations = resources.filter(
    (r: FHIRResource) => r.resourceType === "Observation",
  ).length;
  const allergies = resources.filter(
    (r: FHIRResource) => r.resourceType === "AllergyIntolerance",
  ).length;
  const conditions = resources.filter(
    (r: FHIRResource) => r.resourceType === "Condition",
  ).length;
  const immunizations = resources.filter(
    (r: FHIRResource) => r.resourceType === "Immunization",
  ).length;
  const medications = resources.filter(
    (r: FHIRResource) => r.resourceType === "MedicationRequest",
  ).length;

  if (error) {
    return (
      <div className="p-6">
        <div className="text-center">
          <AlertTriangle className="mx-auto h-12 w-12 text-red-500 mb-4" />
          <h2 className="text-lg font-semibold text-red-600 mb-2">
            {t.fhirxDashboardError}
          </h2>
          <p className="text-gray-600 mb-4">{t.fhirxLoadError}</p>
          <Button onClick={() => refetch()} variant="outline">
            <RefreshCw className="mr-2 h-4 w-4" />
            {t.fhirxTryAgain}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">{t.fhirxDashboardTitle}</h1>
          <p className="text-gray-600 mt-2">{t.fhirxDashboardSubtitle}</p>
        </div>
        <Button
          onClick={() => refetch()}
          variant="outline"
          disabled={isLoading}
        >
          <RefreshCw
            className={`mr-2 h-4 w-4 ${isLoading ? "animate-spin" : ""}`}
          />
          {isLoading ? t.loading : t.refresh}
        </Button>
      </div>

      {/* Statistics Cards */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {/* Total Resources */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t.fhirxTotalResources}
            </CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalResources}</div>
            <p className="text-xs text-muted-foreground">
              {t.fhirxAllResources}
            </p>
          </CardContent>
        </Card>

        {/* Observations */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t.fhirxObservations}
            </CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{observations}</div>
            <p className="text-xs text-muted-foreground">
              {t.fhirxObservationsHint}
            </p>
          </CardContent>
        </Card>

        {/* Allergies */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t.fhirxAllergies}
            </CardTitle>
            <AlertTriangle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{allergies}</div>
            <p className="text-xs text-muted-foreground">
              {t.fhirxAllergiesHint}
            </p>
          </CardContent>
        </Card>

        {/* Conditions */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t.fhirxConditions}
            </CardTitle>
            <Stethoscope className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{conditions}</div>
            <p className="text-xs text-muted-foreground">
              {t.fhirxConditionsHint}
            </p>
          </CardContent>
        </Card>

        {/* Immunizations */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t.fhirxImmunizations}
            </CardTitle>
            <Shield className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{immunizations}</div>
            <p className="text-xs text-muted-foreground">
              {t.fhirxImmunizationsHint}
            </p>
          </CardContent>
        </Card>

        {/* Medications */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t.fhirxMedications}
            </CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{medications}</div>
            <p className="text-xs text-muted-foreground">
              {t.fhirxMedicationsHint}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="text-center py-8">
          <RefreshCw className="mx-auto h-8 w-8 animate-spin text-gray-500 mb-4" />
          <p className="text-gray-500">{t.fhirxLoadingResources}</p>
        </div>
      )}

      {/* No data state */}
      {!isLoading && totalResources === 0 && (
        <div className="text-center py-8">
          <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {t.fhirxNoResources}
          </h3>
          <p className="text-gray-500">{t.fhirxNoResourcesHint}</p>
        </div>
      )}
    </div>
  );
};

export default SimpleFHIRDashboard;
